import {ReactNode, useContext} from 'react';
import {
  FeatureFlags,
  FeatureFlagsProvider,
  useWidgetTriggerParams,
} from '@stripe-internal/conversations-ui';
import {getDefaultViewMode} from '../buildExtractedConfig';
import {ConfigContext} from './ConfigProvider';
import {useProfile} from './ProfileProvider/ProfileProvider';
import {
  isDotComAudience,
  isDotComEntryPoint,
} from '../Screens/ChatScreen/conversationUtils';

type Props = {
  children: ReactNode;
};

export function ConversationsUIFeatureFlagsProvider({children}: Props) {
  const config = useContext(ConfigContext);
  const {profile} = useProfile();
  const {triggerParams} = useWidgetTriggerParams();
  const {featureFlags} = profile;
  const {
    enableUniversalHelp,
    universalHelpUseNewDesign,
    universalHelpDotComFeedbackButtons,
    universalHelpDotComNoLlm,
    universalHelpDotComCsf,
    universalHelpMessagesTopAnchored,
    universalHelpUseInlineRacEmailForm,
    consolidatedConversationsLiveAnswersRow,
    universalHelpEncourageMoreDetailFromUserMse5042,
    enableScreenshareUi,
    enableScreenshareContextProvider,
    enableUhPendingInput,
  } = featureFlags;

  const value: FeatureFlags = {
    newUniversalHelpDesign: enableUniversalHelp && universalHelpUseNewDesign,
    universalHelpDotComDesign:
      enableUniversalHelp &&
      (config.audience === 'dot_com' || isDotComEntryPoint(config.entrypoint)),
    dotComFeedbackButtons:
      enableUniversalHelp && universalHelpDotComFeedbackButtons,
    dotComNoLlm: enableUniversalHelp && universalHelpDotComNoLlm,
    dotComCsf: enableUniversalHelp && universalHelpDotComCsf,
    dotComCsfSelfServe:
      triggerParams?.triggerPoint === 'dot_com_embedded_csf_self_serve',
    messagesTopAnchored:
      enableUniversalHelp && universalHelpMessagesTopAnchored,
    inlineRacEmailForm:
      enableUniversalHelp && universalHelpUseInlineRacEmailForm,
    conversationsLiveAnswersRow:
      consolidatedConversationsLiveAnswersRow ||
      isDotComAudience(config.audience, config.entrypoint),
    universalHelpEncourageMoreDetailFromUserMse5042,
    enableScreenshareUi,
    enableScreenshareContextProvider,
    enableUhPendingInput,
    viewMode: getDefaultViewMode({
      entryPoint: config.entrypoint,
      viewMode: config.viewMode,
    }) as FeatureFlags['viewMode'],
  };

  return <FeatureFlagsProvider value={value}>{children}</FeatureFlagsProvider>;
}
